/**
 * 日ビューに重ねる「今日の過ごし方」（睡眠レポートの今日タブ）
 *
 * 時間帯ごとの調子（集中状態の予測）、集中できる時間、運動におすすめの時間、今日やってみる TODO の提案を
 * 選択日の範囲に切り詰めて返す。表示は TodaySuggestionLayer が行う。
 */

import { format } from 'date-fns'

import type { ExerciseSlot, FocusSlot, TodayAdviceResponse, TodayEnergy, TodayTodoPick } from '@/api/types/sleep'
import type { TodoResponse } from '@/api/types/todo'
import { parseUtcDate } from '@/lib/date-utils'

export type EnergyBand = { id: string; start: Date; end: Date; energy: TodayEnergy; label: string }

/** TODO 列の背景に出す、集中・運動のために取ってある時間 */
export type SlotOverlay = { id: string; start: Date; end: Date; slot: FocusSlot | ExerciseSlot; todoTitles: string[] }

export type PickOverlay = { id: string; start: Date; end: Date; pick: TodayTodoPick }

export type TodayOverlay = {
  bands: EnergyBand[]
  focus: SlotOverlay[]
  exercise: SlotOverlay[]
  picks: PickOverlay[]
}

type Params = {
  today: TodayAdviceResponse | null
  date: Date
  todos: TodoResponse[]
  /** 保留トレイにあるなど、提案として出さない TODO */
  hiddenTodoIds?: number[]
}

const dayRange = (date: Date) => {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const end = new Date(date)
  end.setHours(23, 59, 59, 999)
  return { start, end }
}

/** 選択日と重なる部分だけに切り詰める。重ならなければ null */
const clip = (startIso: string, endIso: string, day: { start: Date; end: Date }) => {
  const start = parseUtcDate(startIso)
  const end = parseUtcDate(endIso)
  if (end <= day.start || start >= day.end) return null
  return { start: start < day.start ? day.start : start, end: end > day.end ? day.end : end }
}

/** 終えた・時間に入れた・保留中・一覧に無い（完了済みや削除済み）TODO は提案として出さない */
const isPickOpen = (pick: TodayTodoPick, todos: TodoResponse[], hiddenTodoIds: number[]) => {
  if (pick.done || pick.scheduled_start || hiddenTodoIds.includes(pick.todo_id)) return false
  const todo = todos.find((t) => t.id === pick.todo_id)
  if (!todo) return false
  return todo.status !== 'done' && todo.status !== 'dismissed' && !todo.scheduled_start
}

export const buildTodayOverlay = ({ today, date, todos, hiddenTodoIds = [] }: Params): TodayOverlay | null => {
  if (!today || today.date !== format(date, 'yyyy-MM-dd')) return null
  const day = dayRange(date)

  // 1 日分の目安があればそれを使う（blocks は作った時刻より後しか無い）
  const sources = today.timeline?.length ? today.timeline : today.blocks
  const bands = sources.flatMap((b, i) => {
    const range = clip(b.start, b.end, day)
    return range ? [{ id: `energy-${i}`, ...range, energy: b.energy, label: b.label }] : []
  })

  const titleOf = (id: number) =>
    todos.find((t) => t.id === id)?.title ?? today.todo_picks.find((p) => p.todo_id === id)?.title
  const focus = (today.focus?.slots ?? []).flatMap((slot) => {
    const range = clip(slot.start, slot.end, day)
    if (!range) return []
    const todoTitles = slot.todo_ids.flatMap((id) => titleOf(id) ?? [])
    return [{ id: `focus-${slot.kind}-${slot.start}`, ...range, slot, todoTitles }]
  })

  const exercise = (today.exercise_slots ?? []).flatMap((slot) => {
    const range = clip(slot.start, slot.end, day)
    return range ? [{ id: `exercise-${slot.kind}-${slot.start}`, ...range, slot, todoTitles: [] }] : []
  })

  const picks = today.todo_picks.flatMap((pick) => {
    if (!pick.suggested_start || !pick.suggested_end || !isPickOpen(pick, todos, hiddenTodoIds)) return []
    const range = clip(pick.suggested_start, pick.suggested_end, day)
    return range ? [{ id: `pick-${pick.todo_id}`, ...range, pick }] : []
  })

  return { bands, focus, exercise, picks }
}

/** TODO 列の右端に集中・運動の帯を出すか（出すならタスクは帯を避けて細くする） */
export const hasSlotLane = (overlay: TodayOverlay | null) =>
  !!overlay && overlay.focus.length + overlay.exercise.length > 0

/** start〜end と一番長く重なる集中・運動の時間。重ならなければ null */
export const findOverlappingSlot = (overlay: TodayOverlay | null, start: Date, end: Date): SlotOverlay | null => {
  if (!overlay) return null
  let best: SlotOverlay | null = null
  let bestMs = 0
  for (const item of [...overlay.focus, ...overlay.exercise]) {
    const ms = Math.min(end.getTime(), item.end.getTime()) - Math.max(start.getTime(), item.start.getTime())
    if (ms > bestMs) {
      best = item
      bestMs = ms
    }
  }
  return best
}
